import { LOCALE_STORAGE_KEY } from '@/hooks/use-language-preference'
import { useAuth } from '@/lib'
import { type OnboardingPatchInputData, useAuthCompleteOnboarding, useAuthPatchOnboarding } from '@jobilla/nexus-sdk'
import axios from 'axios'
import i18n from 'i18next'
import { useEffect, useRef, useState } from 'react'
import { nextUnansweredStep, type OnboardingScreen, type OnboardingStep, onboardingSteps } from './next-unanswered-step'
import type { CompanyDetails } from './screens/company-screen'
import { useTrialSetup } from './use-trial-setup'

type Answers = OnboardingPatchInputData

export function useOnboardingFlow() {
	const { user, refreshSession } = useAuth()
	const patchMutation = useAuthPatchOnboarding()
	const completeMutation = useAuthCompleteOnboarding()
	const [override, setOverride] = useState<OnboardingScreen | null>(null)
	const [languageConfirmed, setLanguageConfirmed] = useState(false)
	const [hasFailed, setHasFailed] = useState(false)
	const completeInFlightRef = useRef(false)

	if (!user) {
		throw new Error('useOnboardingFlow called without an authenticated user')
	}

	const trialSetup = useTrialSetup(user.isSelfSignup)
	const steps = onboardingSteps(user.isSelfSignup)
	const derivedStep: OnboardingStep = nextUnansweredStep({ ...user, languageConfirmed }, trialSetup.progress)
	const currentStep: OnboardingStep = override ?? derivedStep

	const furthestIndex = derivedStep === 'complete' ? steps.length : steps.indexOf(derivedStep)

	const isReachable = (step: OnboardingScreen) => steps.indexOf(step) <= furthestIndex

	const currentIndex = currentStep === 'complete' ? steps.length : steps.indexOf(currentStep)
	const previousStep = steps.slice(0, currentIndex).filter(isReachable).at(-1)
	const nextStep = steps.slice(currentIndex + 1).find(isReachable)

	const goTo = (step: OnboardingScreen) => {
		if (!isReachable(step)) { return }
		setOverride(step === derivedStep ? null : step)
	}

	const goBack = () => {
		if (previousStep) { goTo(previousStep) }
	}

	const goForward = () => {
		if (nextStep) { goTo(nextStep) }
	}

	// Always the step after this one, even when later steps are already answered.
	const advance = () => {
		setOverride(steps[currentIndex + 1] ?? null)
	}

	const completeOnce = async () => {
		if (completeInFlightRef.current) { return }
		completeInFlightRef.current = true
		try {
			await completeMutation.mutateAsync()
		} catch (error) {
			if (!isAlreadyCompletedError(error)) { throw error }
		} finally {
			completeInFlightRef.current = false
		}
	}

	// Without this, a failed request looks like a dead button. Rethrown so it still reaches error reporting.
	const showingFailure = async (action: () => Promise<void>) => {
		setHasFailed(false)
		try {
			await action()
		} catch (error) {
			setHasFailed(true)
			throw error
		}
	}

	const finish = () =>
		showingFailure(async () => {
			await completeOnce()
			await refreshSession()
		})

	// A self-signup whose trial started without onboarding being completed, e.g. after leaving during 3DS.
	const isSetUpButNotCompleted = derivedStep === 'complete' && user.onboardingCompletedAt === null
	useEffect(() => {
		if (isSetUpButNotCompleted) { void finish() }
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSetUpButNotCompleted])

	const saveAnswer = async <K extends keyof Answers>(field: K, value: Answers[K]) => {
		const answer: Answers = { [field]: value }
		await patchMutation.mutateAsync({ data: answer })

		if (field === 'language' && typeof value === 'string') {
			await i18n.changeLanguage(value)
			window.localStorage.setItem(LOCALE_STORAGE_KEY, value)
			setLanguageConfirmed(true)
		}

		// The phone number is an invited user's final step. A self-signup goes on to start their trial.
		if (field === 'phoneNumber' && !user.isSelfSignup) {
			await completeOnce()
		}

		await refreshSession()
		advance()
	}

	const submit = <K extends keyof Answers>(field: K, value: Answers[K]) =>
		showingFailure(() => saveAnswer(field, value))

	const saveCompany = (company: CompanyDetails) =>
		showingFailure(async () => {
			await trialSetup.saveCompany(company)
			advance()
		})

	return {
		currentStep,
		furthestStep: derivedStep,
		steps,
		trialSetup,
		isLoading: trialSetup.isLoading,
		hasFailed: hasFailed || trialSetup.enterFailed,
		submit,
		saveCompany,
		continueSetup: advance,
		finish,
		goTo,
		isReachable,
		goBack,
		canGoBack: previousStep !== undefined,
		goForward,
		canGoForward: nextStep !== undefined,
		isSubmitting: patchMutation.isPending || completeMutation.isPending || trialSetup.isSavingCompany,
	}
}

function isAlreadyCompletedError(error: unknown): boolean {
	return axios.isAxiosError(error) && error.response?.status === 409
}
